// A finished brawl for visitors who haven't signed in, so they see what a brawl is
// before they're asked to sign in. It's a real one: a brawl saved with Export (the
// Markdown of src/export.js) and put at public/samples/sample.md as it is. Without that
// file the chat stays empty.
//
// parseSample reads the Markdown back into turns; drawSample shows them in the group
// chat (src/thread.js) the way a live brawl ends up: bubbles with their confidence and
// vote stars, a poll per round, the judge's notes, and the final answer pinned on top.
import { IDS, parseTags } from "../lib/protocol.js";

// The Markdown of one brawl (an Export of several questions is cut to the first), or
// null when it isn't one.
//   { question, date, names: { SEAT: name }, items, results: { round: text } }
//   items: { kind: "section", label, round } | { kind: "turn", speaker, phase, round, text }
//        | { kind: "note", who: "Judge" | "Moderator", text }
//        | { kind: "final", speaker, meta, text }
export function parseSample(md) {
  const lines = String(md).replace(/\r\n?/g, "\n").split("\n");
  const end = lines.indexOf("---");
  if (end > 0) lines.length = end;
  if (!/^# Chat Brawl: /.test(lines[0] ?? "")) return null;
  // "2026-10-30 · Opus 5.5 (Anthropic) · Sol 6.1 (OpenAI) · gpt-oss 120B (Open weights)"
  const [date, ...seats] = (lines[2] ?? "").split(" · ");
  if (seats.length !== IDS.length) return null;
  const names = Object.fromEntries(IDS.map((id, i) => [id, seats[i].replace(/\s*\([^)]*\)$/, "")]));
  const seatOf = (name) => IDS.find((id) => names[id] === name);

  const items = [];
  const results = {};
  let question = [];
  let section = null; // the "## " part being read
  let phase = null;
  let round = 0;
  let open = null; // the item whose text the next lines belong to
  const add = (item) => {
    items.push(item);
    open = "text" in item ? item : null;
  };
  for (let i = 3; i < lines.length; i++) {
    const line = lines[i];
    const h2 = line.match(/^## (.+)$/);
    if (h2) {
      section = h2[1];
      open = null;
      const r = section.match(/^Round (\d+)$/);
      if (section === "Opening (blind)") (phase = "opening"), (round = 0), add({ kind: "section", label: "Opening · blind", round: 0 });
      else if (r) (phase = "debate"), (round = Number(r[1])), add({ kind: "section", label: section, round });
      continue;
    }
    if (section === "Question") {
      question.push(line);
      continue;
    }
    if (section === "Vote board") {
      const cells = line.split(/(?<!\\)\|/).slice(1, -1).map((c) => c.trim().replace(/\\\|/g, "|"));
      if (/^\d+$/.test(cells[0] ?? "")) results[Number(cells[0])] = cells.at(-1);
      continue;
    }
    if (section === "Final answer") {
      if (!open) {
        const meta = line.match(/^\*(.+)\*$/);
        if (meta) {
          // "Unanimous · spoken by Opus 5.5 · votes: …", or "Spoken by Opus 5.5".
          const by = seatOf(meta[1].match(/spoken by (.+?)(?: · |$)/i)?.[1]) ?? null;
          add({ kind: "final", speaker: by, meta: meta[1], text: "" });
        }
      } else open.text += `${line}\n`;
      continue;
    }
    if (phase == null) continue; // the web sources, and anything else before the turns
    const h3 = line.match(/^### (.+)$/);
    // A heading inside a model's text isn't a new speaker unless it's a seat's name.
    if (h3 && seatOf(h3[1])) {
      add({ kind: "turn", speaker: seatOf(h3[1]), phase, round, text: "" });
      continue;
    }
    const note = line.match(/^> \*\*(Judge|Moderator):\*\* ?(.*)$/);
    if (note) {
      add({ kind: "note", who: note[1], text: `${note[2]}\n` });
      continue;
    }
    if (open?.kind === "note") {
      if (line.startsWith("> ")) open.text += `${line.slice(2)}\n`;
      else open = null;
      continue;
    }
    if (open) open.text += `${line}\n`;
  }
  for (const item of items) if ("text" in item) item.text = item.text.trim();
  const final = items.find((t) => t.kind === "final");
  if (final && !final.speaker) items.splice(items.indexOf(final), 1); // no seat to draw it as
  question = question.join("\n").trim();
  if (!question || !items.some((t) => t.kind === "turn")) return null;
  return { question, date, names, items, results };
}

// Draws `sample` into the chat. `render(text)` turns a turn's text into its HTML (the
// app's renderBody, which drops the tag lines).
export function drawSample(chat, sample, render) {
  const { names } = sample;
  chat.reset();
  chat.members(names);
  chat.system(`A sample brawl, recorded ${sample.date}. Sign in to ask your own question.`, { kind: "note sample" });
  chat.you(sample.question);
  const latest = {};
  const backs = {}; // each model's vote so far
  let votes = null; // this round's: { voter: backed }
  let round = 0;
  const poll = () => {
    if (!votes || !Object.keys(votes).length) return;
    const options = IDS.map((id) => {
      const voters = IDS.filter((v) => votes[v] === id).map((v) => (v === id ? `${names[v]} (itself)` : names[v]));
      return { id, name: names[id], votes: voters.length, voters };
    });
    const result = sample.results[round] ?? "";
    const kind = /^Agreed|same answer/.test(result) ? "agreed" : /differ/.test(result) ? "differ" : "";
    chat.poll(`sample:${round}`, { title: `Round ${round} vote: whose answer is best?`, options, total: Object.keys(votes).length, result, kind });
    votes = null;
  };
  for (const item of sample.items) {
    if (item.kind === "section") {
      poll();
      round = item.round;
      if (item.round) votes = {};
      chat.system(item.label, { kind: "round" });
    } else if (item.kind === "note") {
      if (item.who === "Judge") chat.judge(item.text);
      else chat.you(item.text, "Moderator note");
    } else if (item.kind === "turn") {
      const b = chat.turn(item.speaker, { label: item.phase === "opening" ? "Opening" : `Round ${item.round}` });
      b.paint(render(item.text));
      latest[item.speaker] = b; // first, so a model backing itself stars this turn
      const tags = parseTags(item.text, names);
      if (tags.confidence != null) b.confidence(tags.confidence);
      if (item.phase === "debate" && IDS.includes(tags.endorse)) {
        latest[tags.endorse]?.vote(item.speaker);
        if (votes) votes[item.speaker] = tags.endorse;
        // A change of mind, as a live brawl announces it.
        const before = backs[item.speaker];
        if (before && before !== tags.endorse) {
          chat.system(`${names[item.speaker]} now backs ${tags.endorse === item.speaker ? "itself" : names[tags.endorse]}`, { kind: "switch" });
        }
        backs[item.speaker] = tags.endorse;
      }
    } else if (item.kind === "final") {
      poll();
      const b = chat.turn(item.speaker, { label: "Final answer", final: true });
      b.paint(render(item.text));
      const how = item.meta.split(" · ")[0];
      chat.pinAnswer(b.el, `Final answer by ${names[item.speaker]}${/^spoken by/i.test(how) ? "" : ` · ${how}`}`, { share: false });
    }
  }
  poll();
  chat.sample(true);
}
